import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import customDocumentService from '../../services/api/customDocumentService';
import UploadingModal from '../common/UploadingModal';
import type {
  DocumentTemplateSummary,
  DocumentOutputFormat,
  DocumentGenerationResult,
} from '../../types/customDocuments';

interface GenerateDocumentModalProps {
  clientId: string;
  selectedTradeIds: string[];
  templates: DocumentTemplateSummary[];
  onGenerated: (results: DocumentGenerationResult[]) => void;
  onClose: () => void;
}

const GenerateDocumentModal: React.FC<GenerateDocumentModalProps> = ({
  clientId,
  selectedTradeIds,
  templates,
  onGenerated,
  onClose,
}) => {
  const { t } = useTranslation();
  const sortedTemplates = [...templates].sort((a, b) => a.name.localeCompare(b.name));

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedFormat, setSelectedFormat] = useState<DocumentOutputFormat>('DOCX');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-select first template on mount
  useEffect(() => {
    if (sortedTemplates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(sortedTemplates[0].templateId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const handleGenerate = async () => {
    if (!selectedTemplateId) return;
    setGenerating(true);
    setError(null);
    try {
      const results = await customDocumentService.generateDocuments(clientId, {
        tradeIds: selectedTradeIds,
        templateId: selectedTemplateId,
        outputFormat: selectedFormat,
        trigger: 'MANUAL',
      });
      onGenerated(results);
      onClose();
    } catch {
      setError(t('customDocumentsPage.modal.generateError'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <UploadingModal isOpen={generating} message={t('customDocumentsPage.modal.generating')} />
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="form-panel-header">
            <h4>{t('customDocumentsPage.modal.title')}</h4>
            <button className="btn-close" onClick={onClose}>&times;</button>
          </div>

          <div className="form-panel-body">
            <div className="form-group">
              <label>{t('customDocumentsPage.modal.template')}</label>
              <select
                className="form-input"
                value={selectedTemplateId}
                onChange={e => handleTemplateChange(e.target.value)}
                disabled={generating}
              >
                {sortedTemplates.length === 0 && (
                  <option value="">{t('customDocumentsPage.modal.noTemplates')}</option>
                )}
                {sortedTemplates.map(tpl => (
                  <option key={tpl.templateId} value={tpl.templateId}>
                    {tpl.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('customDocumentsPage.modal.outputFormat')}</label>
              <div className="radio-group">
                {(['DOCX', 'PDF'] as DocumentOutputFormat[]).map(fmt => (
                  <label key={fmt} className="radio-label">
                    <input
                      type="radio"
                      value={fmt}
                      checked={selectedFormat === fmt}
                      onChange={() => setSelectedFormat(fmt)}
                      disabled={generating}
                    />
                    {fmt}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-hint">
              {t('customDocumentsPage.modal.summary', { count: selectedTradeIds.length })}
            </div>

            {error && <div className="form-error">{error}</div>}
          </div>

          <div className="form-panel-footer">
            <button
              className="btn-secondary"
              onClick={onClose}
              disabled={generating}
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              className="btn-primary"
              onClick={handleGenerate}
              disabled={generating || !selectedTemplateId}
            >
              {t('customDocumentsPage.modal.generate')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default GenerateDocumentModal;
